// ---------------------------------------------------------------------------
// Settings → People & access (admins only).
// ---------------------------------------------------------------------------
// ACCESS MODEL (explicit-only, 2026-07-15): membership is the ONE thing that
// grants project visibility. Manager status and task assignments are shown as
// info badges but do not grant access.
//
// Three ways to work:
//   Grid     — the whole people × projects matrix on one screen; click a cell to
//              add/remove membership. The default: manage everyone's access at once
//              instead of opening each project's board.
//   People   — pick a person → what they can see + one-click add to more
//   Projects — pick a project → its roster + one-click add people
// ---------------------------------------------------------------------------

import { useEffect, useMemo, useRef, useState } from 'react';
import { Archive, FolderKanban, LayoutGrid, Mail, Megaphone, Moon, Plus, RotateCcw, Settings, Star, Sun, User, UserRound, X } from 'lucide-react';
import type { AccessMatrix, AccessMatrixUser, MeResponse } from '@cg-projects/shared';
import { api } from '@/core/api/apiClient';
import { Icon } from '@/core/ui/icons';
import { useUiStore } from '@/core/ui/uiStore';
import { useToastStore } from '@/core/ui/toastStore';
import { Spinner } from '@/core/ui/atoms';
import { initials, avatarColor } from '@/core/ui/helpers';
import { Avatar } from '@/core/ui/atoms';
import { useEscapeToClose } from '@/core/ui/useEscapeToClose';
import { useAreaStore } from '@/features/areas/store/areaStore';
import { useAuthStore } from '@/features/auth/store/authStore';
import { setTheme, useTheme } from '@/core/ui/theme';

type Tab = 'grid' | 'people' | 'projects' | 'archived' | 'account';

function Chip({ children, tone = 'muted', title }: { children: React.ReactNode; tone?: 'muted' | 'brand' | 'green'; title?: string }) {
  const cls = tone === 'brand' ? 'bg-brand/15 text-brand' : tone === 'green' ? 'bg-(--chip-green-bg) text-(--chip-green-ink)' : 'bg-hover text-muted';
  return <span title={title} className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${cls}`}>{children}</span>;
}

function PersonDot({ name, id, size = 6 }: { name: string; id?: number; size?: number }) {
  if (id) return <span className="shrink-0"><Avatar person={{ id, name, avatarUrl: null }} size={size * 4} /></span>;
  return (
    <span
      className="inline-flex shrink-0 items-center justify-center rounded-full text-[9px] font-medium text-white"
      style={{ backgroundColor: avatarColor(name), width: size * 4, height: size * 4 }}
    >
      {initials(name)}
    </span>
  );
}

/** "3d ago" from an Odoo datetime — admins scanning who actually signs in. */
function lastSeen(iso?: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso.replace(' ', 'T') + 'Z');
  if (Number.isNaN(d.getTime())) return null;
  const days = Math.floor((Date.now() - d.getTime()) / 86_400_000);
  if (days <= 0) return 'today';
  if (days === 1) return '1d ago';
  if (days < 60) return days + 'd ago';
  return Math.floor(days / 30) + 'mo ago';
}

export function SettingsPanel() {
  const close = useUiStore((s) => s.closeSettings);
  const subOpenRef = useRef(false);
  // Esc with a sub-dialog open must close ONLY the dialog (it has its own
  // handler) — not the whole Settings screen underneath it.
  useEscapeToClose(() => { if (!subOpenRef.current) close(); });
  const toast = useToastStore((s) => s.show);
  // Settings is no longer admin-only: everyone owns a "My account" tab (email
  // preferences), so non-admins open HERE and see ONLY here — the access tabs
  // and their /admin fetch never exist for them.
  const isAdmin = Boolean(useAuthStore((s) => s.user?.isAdmin));
  const [matrix, setMatrix] = useState<AccessMatrix | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>(isAdmin ? 'grid' : 'account');
  // Writes in flight — a COUNT, not a boolean: the grid tab fires per-cell writes
  // concurrently, and the first to finish must not clear the flag while a second
  // is still running. `busy` still reads true for the list tabs exactly as before.
  const [busyCount, setBusyCount] = useState(0);
  const busy = busyCount > 0;
  const [offboard, setOffboard] = useState<{ u: AccessMatrixUser; areaId: number } | null>(null);
  const [announceOpen, setAnnounceOpen] = useState(false);
  useEffect(() => { subOpenRef.current = announceOpen || offboard !== null; }, [announceOpen, offboard]);

  /** Local matrix update after a completed offboard (dialog already saved). */
  const applyOffboard = (userId: number, areaId: number, opts: { unassigned: boolean; newManagerUid: number | null }) => {
    setMatrix((m) => m && ({
      ...m,
      users: m.users.map((x) => {
        let out = x;
        if (x.user.id === userId) {
          const assignedIn = { ...x.assignedIn };
          if (opts.unassigned) delete assignedIn[areaId];
          out = { ...x, memberOf: x.memberOf.filter((id) => id !== areaId), managerOf: x.managerOf.filter((id) => id !== areaId), assignedIn };
        }
        if (opts.newManagerUid && x.user.id === opts.newManagerUid) {
          out = { ...out, managerOf: [...new Set([...out.managerOf, areaId])], memberOf: [...new Set([...out.memberOf, areaId])] };
        }
        return out;
      }),
    }));
  };

  /** Hand a project to a new lead (they become a member automatically). */
  const changeLead = async (areaId: number, uid: number) => {
    if (busy) return;
    setBusyCount((n) => n + 1);
    try {
      await api.patch(`/areas/${areaId}`, { managerId: uid });
      setMatrix((m) => m && ({
        ...m,
        users: m.users.map((x) => {
          const isNew = x.user.id === uid;
          return {
            ...x,
            managerOf: isNew ? [...new Set([...x.managerOf, areaId])] : x.managerOf.filter((id) => id !== areaId),
            memberOf: isNew ? [...new Set([...x.memberOf, areaId])] : x.memberOf,
          };
        }),
      }));
      toast('Project lead changed.', 'info');
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Could not change the lead.', 'error');
    } finally {
      setBusyCount((n) => n - 1);
    }
  };

  useEffect(() => {
    if (!isAdmin) return;
    api.get<{ matrix: AccessMatrix }>('/admin/access')
      .then((r) => setMatrix(r.matrix))
      .catch((e) => setError(e instanceof Error ? e.message : 'Could not load access data.'));
  }, [isAdmin]);

  // ---- shared mutations (optimistic on the matrix) ----
  // No `if (busy) return` guard on add/remove: the grid fires these per-cell and
  // concurrently (its own pending set blocks a double-click on the SAME cell),
  // and the list tabs disable their buttons while `busy`, so they can't overlap.
  const addMember = async (userId: number, areaId: number) => {
    setBusyCount((n) => n + 1);
    try {
      await api.post(`/areas/${areaId}/members`, { userId });
      setMatrix((m) => m && ({
        ...m,
        users: m.users.map((u) => (u.user.id === userId ? { ...u, memberOf: [...new Set([...u.memberOf, areaId])] } : u)),
      }));
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Could not add.', 'error');
    } finally {
      setBusyCount((n) => n - 1);
    }
  };

  const removeMember = async (userId: number, areaId: number) => {
    setBusyCount((n) => n + 1);
    try {
      await api.delete(`/areas/${areaId}/members/${userId}`);
      setMatrix((m) => m && ({
        ...m,
        users: m.users.map((u) => (u.user.id === userId ? { ...u, memberOf: u.memberOf.filter((id) => id !== areaId) } : u)),
      }));
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Could not remove.', 'error');
    } finally {
      setBusyCount((n) => n - 1);
    }
  };

  const removeAll = async (u: AccessMatrixUser) => {
    if (busy || !u.memberOf.length) return;
    // Projects they LEAD need a new lead first — never leave a dead lead behind.
    const managed = u.memberOf.filter((id) => u.managerOf.includes(id));
    const removable = u.memberOf.filter((id) => !u.managerOf.includes(id));
    // Count what will be handed back so the toast can say so.
    const openTasks = removable.reduce((n, id) => n + (u.assignedIn?.[id] ?? 0), 0);
    setBusyCount((n) => n + 1);
    try {
      for (const areaId of removable) {
        // Same path as the single-project offboard — plain member-delete would
        // leave their open tasks assigned to someone with no access to them.
        await api.post('/admin/remove-access', { userId: u.user.id, areaId, reassignToUserId: null });
      }
      setMatrix((m) => m && ({
        ...m,
        users: m.users.map((x) => {
          if (x.user.id !== u.user.id) return x;
          const assignedIn = { ...x.assignedIn };
          for (const id of removable) delete assignedIn[id];
          return { ...x, memberOf: managed, assignedIn };
        }),
      }));
      const tail = openTasks ? ` ${openTasks} open task(s) were unassigned.` : '';
      toast(
        managed.length
          ? `${u.user.name} removed from ${removable.length} project(s).${tail} ${managed.length} they LEAD were kept — open each and pick a new lead first.`
          : `${u.user.name} removed from all projects.${tail}`,
        'info',
      );
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Bulk remove hit an error.', 'error');
      // Partial failure: re-fetch the truth instead of showing a stale matrix.
      try {
        const r = await api.get<{ matrix: AccessMatrix }>('/admin/access');
        setMatrix(r.matrix);
      } catch { /* keep whatever we have */ }
    } finally {
      setBusyCount((n) => n - 1);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-page animate-fade-in">
      <div className="flex h-12 shrink-0 items-center justify-between border-b border-line px-4">
        <div className="flex items-center gap-4">
          <span className="flex items-center gap-1.5 text-sm font-semibold text-ink"><Icon i={Settings} size={16} />Settings</span>
          <nav className="flex items-center gap-0.5 rounded-md border border-line bg-canvas p-0.5">
            {(isAdmin ? (['grid', 'people', 'projects', 'archived', 'account'] as Tab[]) : (['account'] as Tab[])).map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`flex items-center gap-1.5 rounded px-3 py-1 text-xs font-medium capitalize transition ${tab === t ? 'bg-active text-ink' : 'text-muted hover:text-ink'}`}
              >
                <Icon i={t === 'grid' ? LayoutGrid : t === 'people' ? User : t === 'projects' ? FolderKanban : t === 'archived' ? Archive : UserRound} size={14} />
                {t === 'grid' ? 'Members grid' : t === 'people' ? 'By person' : t === 'projects' ? 'By project' : t === 'archived' ? 'Archived' : 'My account'}
              </button>
            ))}
          </nav>
          <span className="hidden text-[11px] text-faint sm:block">Access = membership. Tasks & manager status are info only.</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setAnnounceOpen(true)}
            className="flex items-center gap-1.5 rounded-md border border-brand/40 bg-brand/10 px-2.5 py-1 text-xs font-medium text-brand transition hover:bg-brand/20"
            title="Send a note to EVERY team member's inbox"
          >
            <Icon i={Megaphone} size={14} />
            Announce
          </button>
          <button onClick={close} className="flex rounded p-1 text-muted hover:bg-hover hover:text-ink" title="Close"><Icon i={X} size={16} /></button>
        </div>
      </div>
      {announceOpen && <AnnounceModal onClose={() => setAnnounceOpen(false)} />}

      {error && tab !== 'account' && <div className="p-6 text-sm text-danger">{error}</div>}
      {tab === 'account' ? (
        <AccountView />
      ) : tab === 'archived' ? (
        <ArchivedView />
      ) : (
        <>
          {!matrix && !error && <div className="flex flex-1 items-center justify-center"><Spinner label="Loading access data…" /></div>}
          {matrix && (tab === 'grid'
            ? <GridView matrix={matrix} addMember={addMember} removeMember={removeMember} />
            : tab === 'people'
            ? <PeopleView matrix={matrix} busy={busy} addMember={addMember} removeMember={removeMember} removeAll={removeAll} openOffboard={(u, areaId) => setOffboard({ u, areaId })} />
            : <ProjectsView matrix={matrix} busy={busy} addMember={addMember} removeMember={removeMember} openOffboard={(u, areaId) => setOffboard({ u, areaId })} changeLead={changeLead} />)}
        </>
      )}
      {offboard && matrix && (
        <OffboardDialog
          u={offboard.u}
          areaId={offboard.areaId}
          areaName={matrix.areas.find((a) => a.id === offboard.areaId)?.name ?? `#${offboard.areaId}`}
          matrix={matrix}
          onDone={(opts) => { applyOffboard(offboard.u.user.id, offboard.areaId, opts); setOffboard(null); }}
          onClose={() => setOffboard(null)}
          refetchTruth={() => {
            // Partial failure (e.g. lead handed over but removal failed) —
            // show Odoo's real state, never a stale local guess.
            api.get<{ matrix: AccessMatrix }>('/admin/access').then((r) => setMatrix(r.matrix)).catch(() => undefined);
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
/** Offboarding — removing someone who still LEADS the project or HOLDS open
 *  tasks here shouldn't strand either. One dialog settles both. */
function OffboardDialog({ u, areaId, areaName, matrix, onDone, onClose, refetchTruth }: {
  u: AccessMatrixUser;
  areaId: number;
  areaName: string;
  matrix: AccessMatrix;
  onDone: (opts: { unassigned: boolean; newManagerUid: number | null }) => void;
  onClose: () => void;
  refetchTruth: () => void;
}) {
  useEscapeToClose(onClose);
  const toast = useToastStore((s) => s.show);
  const isManager = u.managerOf.includes(areaId);
  const openTasks = u.assignedIn[areaId] ?? 0;
  const [mode, setMode] = useState<'keep' | 'unassign' | 'reassign'>(openTasks > 0 ? 'unassign' : 'keep');
  const [reassignTo, setReassignTo] = useState<number>(0);
  const [newLead, setNewLead] = useState<number>(0);
  const [busy, setBusy] = useState(false);

  // Inherit-candidates must be able to SEE the project (member or admin).
  const candidates = matrix.users
    .filter((x) => x.user.id !== u.user.id && (x.isAdmin || x.memberOf.includes(areaId)))
    .sort((a, b) => a.user.name.localeCompare(b.user.name));

  const confirm = async () => {
    if (busy) return;
    if (mode === 'reassign' && !reassignTo) { toast('Pick who inherits their tasks.', 'error'); return; }
    if (isManager && !newLead) { toast('Pick the new project lead first.', 'error'); return; }
    setBusy(true);
    try {
      if (mode === 'keep') {
        // Tasks stay assigned; lead handoff (if any) then plain membership off.
        if (isManager) await api.patch(`/areas/${areaId}`, { managerId: newLead });
        await api.delete(`/areas/${areaId}/members/${u.user.id}`);
      } else {
        await api.post('/admin/remove-access', {
          userId: u.user.id,
          areaId,
          reassignToUserId: mode === 'reassign' ? reassignTo : null,
          newManagerUid: isManager ? newLead : null,
        });
      }
      toast(`${u.user.name} removed from "${areaName}".`, 'info');
      onDone({ unassigned: mode !== 'keep', newManagerUid: isManager ? newLead : null });
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Could not remove access.', 'error');
      refetchTruth(); // the two-step 'keep' path may have half-applied
    } finally {
      setBusy(false);
    }
  };

  const radio = (value: typeof mode, label: string, hint: string, disabled = false) => (
    <label className={`flex items-start gap-2 rounded-md border px-3 py-2 ${mode === value ? 'border-brand/60 bg-brand/5' : 'border-line'} ${disabled ? 'opacity-40' : 'cursor-pointer hover:bg-hover'}`}>
      <input type="radio" checked={mode === value} disabled={disabled} onChange={() => setMode(value)} className="mt-0.5 accent-brand" />
      <span>
        <span className="block text-[13px] text-ink">{label}</span>
        <span className="block text-[11px] text-faint">{hint}</span>
      </span>
    </label>
  );

  return (
    <>
      <div className="fixed inset-0 z-[70] bg-black/60 animate-fade-in" onClick={onClose} />
      <div className="fixed inset-x-4 top-20 z-[71] mx-auto max-w-md rounded-xl border border-line bg-canvas p-4 shadow-2xl animate-fade-in">
        <div className="mb-1 text-sm font-semibold text-ink">Remove {u.user.name} from “{areaName}”</div>
        <div className="mb-3 text-[12px] text-muted">
          {openTasks > 0 ? `They hold ${openTasks} open task${openTasks > 1 ? 's' : ''} here.` : 'They hold no open tasks here.'}
          {isManager && ' They also LEAD this project.'}
        </div>

        <div className="space-y-1.5">
          {radio('keep', 'Keep their tasks assigned', 'They lose access but stay on the tasks (e.g. temporary removal).')}
          {radio('unassign', 'Unassign their open tasks', 'Tasks become unassigned — visible in “Unassigned” filters.', openTasks === 0)}
          {radio('reassign', 'Hand their open tasks to…', 'Everything open moves to one person.', openTasks === 0)}
        </div>
        {mode === 'reassign' && (
          <select value={reassignTo} onChange={(e) => setReassignTo(Number(e.target.value))}
            className="mt-2 w-full rounded-md border border-line bg-page px-2 py-1.5 text-[13px] text-ink outline-none">
            <option value={0}>Pick a person…</option>
            {candidates.map((c) => <option key={c.user.id} value={c.user.id}>{c.user.name}</option>)}
          </select>
        )}

        {isManager && (
          <div className="mt-3 rounded-md border border-brand/40 bg-brand/10 p-2">
            <div className="mb-1 text-[12px] font-semibold text-brand">New project lead (required)</div>
            <select value={newLead} onChange={(e) => setNewLead(Number(e.target.value))}
              className="w-full rounded-md border border-line bg-page px-2 py-1.5 text-[13px] text-ink outline-none">
              <option value={0}>Pick the new lead…</option>
              {candidates.map((c) => <option key={c.user.id} value={c.user.id}>{c.user.name}</option>)}
            </select>
          </div>
        )}

        <div className="mt-4 flex items-center justify-end gap-2">
          <button onClick={onClose} className="text-[12px] text-muted hover:text-ink">Cancel</button>
          <button onClick={() => void confirm()} disabled={busy}
            className="rounded-md bg-danger-solid px-3 py-1.5 text-[12px] font-semibold text-white hover:brightness-110 disabled:opacity-50">
            {busy ? 'Removing…' : 'Remove access'}
          </button>
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
/** 📣 One note → every team member's inbox (+ a live toast in open tabs). */
function AnnounceModal({ onClose }: { onClose: () => void }) {
  useEscapeToClose(onClose);
  const toast = useToastStore((s) => s.show);
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);

  const send = async () => {
    const text = body.trim();
    if (!text || busy) return;
    if (!window.confirm(`Send this to EVERYONE's inbox?\n\n"${text.slice(0, 120)}${text.length > 120 ? '…' : ''}"\n\nHeads up: people whose Odoo preference is "Email" also get this as an email.`)) return;
    setBusy(true);
    try {
      const { notified } = await api.post<{ notified: number }>('/admin/announce', { body: text }, { timeout: 120000 });
      toast(`Sent — ${notified} people notified.`, 'info');
      onClose();
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Could not send the announcement.', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-[70] bg-black/60 animate-fade-in" onClick={onClose} />
      <div className="fixed inset-x-4 top-24 z-[71] mx-auto max-w-md rounded-xl border border-line bg-canvas p-4 shadow-2xl animate-fade-in">
        <div className="mb-1 flex items-center gap-1.5 text-sm font-semibold text-ink"><Icon i={Megaphone} size={14} /> Announce to everyone</div>
        <p className="mb-2 text-[11px] text-faint">Lands in every team member's inbox (and pops up live in open tabs). Plain text, no formatting.</p>
        <textarea
          autoFocus
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && !e.repeat) { e.preventDefault(); void send(); } }}
          rows={4}
          maxLength={2000}
          placeholder="e.g. All-hands Friday 9am · New PTO policy is live · Orlando store closed Monday"
          className="w-full resize-none rounded-md border border-line bg-page px-2.5 py-1.5 text-[13px] text-ink outline-none placeholder:text-faint focus:border-brand"
        />
        <div className="mt-3 flex items-center justify-end gap-2">
          <button onClick={onClose} className="text-[12px] text-muted hover:text-ink">Cancel</button>
          <button onClick={() => void send()} disabled={busy || !body.trim()}
            className="rounded-md bg-brand px-3 py-1.5 text-[12px] font-semibold text-page hover:brightness-110 disabled:opacity-50">
            {busy ? 'Sending…' : 'Send to everyone'}
          </button>
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
/** My account — the viewer's OWN preferences; every other tab here is
 *  admin-facing. Today: the personal email opt-out for app notifications. */
function AccountView() {
  const user = useAuthStore((s) => s.user);
  const toast = useToastStore((s) => s.show);
  // Same source as the sidebar footer toggle — the two controls mirror each other.
  const theme = useTheme();
  // Default-on: an absent field (older session payloads, or the preference
  // layer not landed yet) must read as on, never as opted out.
  const [emailOn, setEmailOn] = useState<boolean>(user?.emailNotifications ?? true);
  const [busy, setBusy] = useState(false);

  // The sign-in payload can predate the preference — read the fresh value once.
  useEffect(() => {
    let alive = true;
    api.get<MeResponse>('/auth/me')
      .then((r) => { if (alive) setEmailOn(r.user.emailNotifications ?? true); })
      .catch(() => undefined);
    return () => { alive = false; };
  }, []);

  const toggle = async () => {
    if (busy) return;
    const next = !emailOn;
    setEmailOn(next); // optimistic — rolled back below if the save fails
    setBusy(true);
    try {
      await api.patch('/auth/me', { emailNotifications: next });
      // Write through to the session user so reopening Settings seeds correctly.
      const u = useAuthStore.getState().user;
      if (u) useAuthStore.setState({ user: { ...u, emailNotifications: next } });
    } catch (e) {
      setEmailOn(!next);
      toast(e instanceof Error ? e.message : 'Could not save that — try again.', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-0 flex-1 overflow-y-auto">
      <div className="mx-auto max-w-2xl px-4 py-5">
        {user && (
          <div className="mb-4 flex items-center gap-2">
            <PersonDot name={user.name} id={user.uid} size={8} />
            <div className="min-w-0">
              <div className="truncate text-[15px] font-semibold text-ink">{user.name}</div>
              <div className="truncate text-[11px] text-faint">{user.email}</div>
            </div>
          </div>
        )}
        <section>
          <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-faint">Notifications</h3>
          <div className="flex items-center gap-3 rounded-lg border border-line bg-canvas px-3 py-2.5">
            <Icon i={Mail} size={16} className="shrink-0 text-muted" />
            <div className="min-w-0 flex-1">
              <div className="text-[13px] text-ink">Email me notifications</div>
              <div className="text-[11px] text-faint">Mentions, assignments, and stage changes — sent to {user?.email || 'your email'} as well as your inbox here.</div>
            </div>
            <button
              role="switch"
              aria-checked={emailOn}
              disabled={busy}
              onClick={() => void toggle()}
              title={emailOn ? 'On — click to stop the emails' : 'Off — click to get emails again'}
              className={`relative h-5 w-9 shrink-0 rounded-full transition disabled:opacity-50 ${emailOn ? 'bg-success' : 'bg-hover'}`}
            >
              <span className={`absolute top-0.5 h-4 w-4 rounded-full transition-all ${emailOn ? 'left-[18px] bg-page' : 'left-0.5 bg-faint'}`} />
            </button>
          </div>
          <p className="mt-1.5 text-[11px] text-faint">You always get these in the app — this only controls the email copies.</p>
        </section>

        <section className="mt-5">
          <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-faint">Appearance</h3>
          <div className="flex items-center gap-3 rounded-lg border border-line bg-canvas px-3 py-2.5">
            <Icon i={theme === 'light' ? Sun : Moon} size={16} className="shrink-0 text-muted" />
            <div className="min-w-0 flex-1">
              <div className="text-[13px] text-ink">Theme</div>
              <div className="text-[11px] text-faint">Dark is the standard look. Light is the cream look, like the Inventory app.</div>
            </div>
            <div className="flex shrink-0 items-center gap-0.5 rounded-md border border-line p-0.5">
              {(['dark', 'light'] as const).map((t) => (
                <button
                  key={t}
                  onClick={() => setTheme(t)}
                  aria-pressed={theme === t}
                  title={t === 'dark' ? 'Deep green on near-black — the default' : 'Dark ink on cream'}
                  className={`flex items-center gap-1.5 rounded px-3 py-1 text-xs font-medium capitalize transition ${theme === t ? 'bg-active text-ink' : 'text-muted hover:text-ink'}`}
                >
                  <Icon i={t === 'dark' ? Moon : Sun} size={14} /> {t}
                </button>
              ))}
            </div>
          </div>
          <p className="mt-1.5 text-[11px] text-faint">Saved on this device only. The sun/moon at the bottom of the sidebar flips this too.</p>
        </section>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
/** Archived projects — the way back after "Archive project". Admin-only tab. */
function ArchivedView() {
  const toast = useToastStore((s) => s.show);
  const [rows, setRows] = useState<Array<{ id: number; name: string }> | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = () => {
    api.get<{ areas: Array<{ id: number; name: string }> }>('/areas/archived')
      .then((r) => setRows(r.areas))
      .catch((e) => { setRows([]); toast(e instanceof Error ? e.message : 'Could not load archived projects.', 'error'); });
  };
  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps

  const restore = async (id: number, name: string) => {
    if (busyId) return;
    setBusyId(id);
    try {
      await api.post(`/areas/${id}/unarchive`, {});
      setRows((prev) => (prev ?? []).filter((a) => a.id !== id));
      toast(`"${name}" is back — it's live in the sidebar again.`, 'info');
      void useAreaStore.getState().loadAreas(true);
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Could not restore.', 'error');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="min-h-0 flex-1 overflow-y-auto">
      <div className="mx-auto max-w-2xl px-4 py-5">
        <p className="mb-3 text-[12px] text-faint">Archived projects are hidden everywhere. Restore brings one back exactly as it was — tasks, members, history.</p>
        {rows === null && <Spinner label="Loading archived projects…" />}
        {rows !== null && rows.length === 0 && <div className="py-8 text-center text-sm text-faint">Nothing archived. 🎉</div>}
        {(rows ?? []).map((a) => (
          <div key={a.id} className="mb-1.5 flex items-center gap-3 rounded-md border border-line bg-canvas px-3 py-2">
            <span className="min-w-0 flex-1 truncate text-[13px] text-ink">{a.name}</span>
            <button
              onClick={() => void restore(a.id, a.name)}
              disabled={busyId !== null}
              className="flex items-center gap-1 rounded-md border border-line px-2.5 py-1 text-xs text-muted transition hover:bg-hover hover:text-ink disabled:opacity-40"
            >
              {busyId === a.id ? 'Restoring…' : <><Icon i={RotateCcw} size={14} /> Restore</>}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
/** The whole people × projects access map on one screen. Rows are people, columns
 *  are projects; a filled cell means membership. Click any cell to add or remove —
 *  no drilling into a project's board, no one-at-a-time. Leads (★) and admins (who
 *  see everything) are shown but not toggleable here: their access is structural,
 *  not a membership row. An amber ring flags someone who HAS tasks in a project but
 *  isn't a member — a real access gap Odoo never surfaced. */
function GridView({ matrix, addMember, removeMember }: {
  matrix: AccessMatrix;
  addMember: (userId: number, areaId: number) => Promise<void>;
  removeMember: (userId: number, areaId: number) => Promise<void>;
}) {
  const [pq, setPq] = useState('');
  const [aq, setAq] = useState('');
  // Per-cell in-flight writes ("userId:areaId"). One write locks ONLY its own
  // cell — not all ~1,900 — so bulk access edits don't freeze the whole matrix
  // between clicks. The pending cell pulses until Odoo answers.
  const [pending, setPending] = useState<Set<string>>(new Set());
  // Admins float to the top; everyone else alphabetical. Projects alphabetical.
  const people = useMemo(
    () => [...matrix.users].sort((a, b) => Number(b.isAdmin) - Number(a.isAdmin) || a.user.name.localeCompare(b.user.name)),
    [matrix.users],
  );
  const areas = useMemo(() => [...matrix.areas].sort((a, b) => a.name.localeCompare(b.name)), [matrix.areas]);
  const shownPeople = people.filter((u) => u.user.name.toLowerCase().includes(pq.trim().toLowerCase()));
  const shownAreas = areas.filter((a) => a.name.toLowerCase().includes(aq.trim().toLowerCase()));

  const colMembers = (areaId: number) =>
    matrix.users.filter((u) => u.isAdmin || u.memberOf.includes(areaId) || u.managerOf.includes(areaId)).length;
  const rowCount = (u: AccessMatrixUser) =>
    u.isAdmin ? matrix.areas.length : new Set([...u.memberOf, ...u.managerOf]).size;

  type Cell = 'admin' | 'manager' | 'member' | 'assigned' | 'none';
  const cellFor = (u: AccessMatrixUser, areaId: number): Cell =>
    u.isAdmin ? 'admin'
      : u.managerOf.includes(areaId) ? 'manager'
        : u.memberOf.includes(areaId) ? 'member'
          : (u.assignedIn?.[areaId] ?? 0) > 0 ? 'assigned'
            : 'none';

  const toggle = async (u: AccessMatrixUser, areaId: number, c: Cell) => {
    if (c === 'admin' || c === 'manager') return; // structural access — not a membership toggle
    const key = `${u.user.id}:${areaId}`;
    if (pending.has(key)) return; // THIS cell already has a write in flight
    setPending((prev) => new Set(prev).add(key));
    try {
      if (c === 'member') await removeMember(u.user.id, areaId);
      else await addMember(u.user.id, areaId);
    } finally {
      setPending((prev) => { const next = new Set(prev); next.delete(key); return next; });
    }
  };

  const SUCCESS = 'var(--color-success)';
  const BRAND = 'var(--color-brand)';

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex flex-wrap items-center gap-2 border-b border-line px-4 py-2.5">
        <input value={pq} onChange={(e) => setPq(e.target.value)} placeholder="Filter people…"
          className="w-44 rounded-md border border-line bg-canvas px-2.5 py-1 text-[13px] text-ink outline-none placeholder:text-faint focus:border-brand" />
        <input value={aq} onChange={(e) => setAq(e.target.value)} placeholder="Filter projects…"
          className="w-44 rounded-md border border-line bg-canvas px-2.5 py-1 text-[13px] text-ink outline-none placeholder:text-faint focus:border-brand" />
        <span className="ml-auto flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted">
          <span className="flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: SUCCESS }} /> member</span>
          <span className="flex items-center gap-1"><span style={{ color: BRAND }}><Icon i={Star} size={14} className="fill-current" /></span> lead</span>
          <span className="flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rounded-full" style={{ boxShadow: `inset 0 0 0 1.5px ${BRAND}` }} /> has tasks, not a member</span>
          <span className="text-faint">· click a cell to add / remove</span>
        </span>
      </div>
      <div className="min-h-0 flex-1 overflow-auto">
        {shownPeople.length === 0 || shownAreas.length === 0 ? (
          <div className="p-10 text-center text-sm text-faint">No {shownPeople.length === 0 ? 'people' : 'projects'} match that filter.</div>
        ) : (
          <table className="border-separate" style={{ borderSpacing: 0 }}>
            <thead>
              <tr>
                <th className="sticky left-0 top-0 z-30 border-b border-r border-line bg-page px-3 py-2 text-left text-[11px] font-semibold text-muted" style={{ minWidth: 210 }}>
                  {shownPeople.length} {shownPeople.length === 1 ? 'person' : 'people'} · {shownAreas.length} projects
                </th>
                {shownAreas.map((a) => (
                  <th key={a.id} className="sticky top-0 z-20 border-b border-line bg-page p-0 align-bottom" style={{ width: 30 }}>
                    <div className="mx-auto flex h-[136px] w-[30px] flex-col items-center justify-end pb-1">
                      <span
                        className="text-[11px] text-muted"
                        style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', maxHeight: 112, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        title={a.name}
                      >
                        {a.name}
                      </span>
                      <span className="mt-1 text-[9px] text-faint">{colMembers(a.id)}</span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {shownPeople.map((u) => (
                <tr key={u.user.id} className="group">
                  <th className="sticky left-0 z-10 border-b border-r border-line bg-page px-3 py-1.5 text-left group-hover:bg-hover" style={{ minWidth: 210 }}>
                    <div className="flex items-center gap-2">
                      <PersonDot name={u.user.name} id={u.user.id} size={6} />
                      <span className="truncate text-[13px] text-ink" style={{ maxWidth: 128 }}>{u.user.name}</span>
                      {u.isAdmin && <span className="rounded bg-brand/15 px-1 text-[9px] font-medium text-brand" title="Admin — sees every project">admin</span>}
                      <span className="ml-auto text-[10px] text-faint" title="Projects this person can see">{rowCount(u)}</span>
                    </div>
                  </th>
                  {shownAreas.map((a) => {
                    const c = cellFor(u, a.id);
                    const isPending = pending.has(`${u.user.id}:${a.id}`);
                    const label = c === 'admin' ? 'Admin — sees all projects'
                      : c === 'manager' ? `Lead of ${a.name}`
                        : c === 'member' ? `Member of ${a.name} — click to remove`
                          : c === 'assigned' ? `Has ${u.assignedIn[a.id] ?? 0} task(s) here but is not a member — click to add`
                            : `Add ${u.user.name} to ${a.name}`;
                    return (
                      <td key={a.id} className="border-b border-line/60 p-0 text-center">
                        <button
                          type="button"
                          disabled={isPending || c === 'admin' || c === 'manager'}
                          onClick={() => void toggle(u, a.id, c)}
                          title={label}
                          className="flex h-7 w-[30px] items-center justify-center transition hover:bg-hover disabled:cursor-default group-hover:bg-hover/40"
                        >
                          {isPending ? <span className="inline-block h-2.5 w-2.5 animate-pulse rounded-full opacity-60" style={{ background: SUCCESS }} />
                            : c === 'admin' ? <span className="text-[9px] text-faint">·</span>
                              : c === 'manager' ? <span style={{ color: BRAND }}><Icon i={Star} size={14} className="fill-current" /></span>
                                : c === 'member' ? <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: SUCCESS }} />
                                  : c === 'assigned' ? <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ boxShadow: `inset 0 0 0 1.5px ${BRAND}` }} />
                                    : <span className="text-sm text-faint opacity-0 transition group-hover:opacity-50">+</span>}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
/** Why CG Team keeps someone out, in words (one login, v1.94.0). */
function teamRefusalWords(r: AccessMatrixUser['teamRefusal']): string {
  switch (r) {
    case 'projects_off': return 'Projects is switched off for them in CG Team.';
    case 'team_login_off': return 'their CG Team login is switched off.';
    case 'no_team_record': return 'this account isn’t linked to anyone in CG Team.';
    default: return 'CG Team does not let them in.';
  }
}

function PeopleView({ matrix, busy, addMember, removeMember, removeAll, openOffboard }: {
  matrix: AccessMatrix;
  busy: boolean;
  addMember: (userId: number, areaId: number) => Promise<void>;
  removeMember: (userId: number, areaId: number) => Promise<void>;
  removeAll: (u: AccessMatrixUser) => Promise<void>;
  openOffboard: (u: AccessMatrixUser, areaId: number) => void;
}) {
  const [q, setQ] = useState('');
  const [projQ, setProjQ] = useState('');
  const [selectedUid, setSelectedUid] = useState<number | null>(null);
  const [confirmAll, setConfirmAll] = useState(false);
  const [confirmSignOut, setConfirmSignOut] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [tempPw, setTempPw] = useState<{ name: string; pw: string } | null>(null);
  const toast = useToastStore((s) => s.show);
  // One login (v1.94.0): no passwords live here, so no password buttons.
  const passwordSignIn = useAuthStore((s) => s.options?.passwordSignIn === true);

  useEffect(() => { setConfirmAll(false); setConfirmSignOut(false); setProjQ(''); setTempPw(null); }, [selectedUid]);

  /** Break-glass for a locked-out teammate: set a temporary password they can sign
   *  in with, shown once for the admin to relay privately. This is the safety net
   *  that makes losing Odoo survivable — before it, a forgotten password after the
   *  cutover had no in-app recovery. The server refuses (409 ODOO_OWNS_AUTH) while
   *  Odoo still owns sign-in, so pre-flip the toast tells the admin to use Odoo. */
  const resetPassword = async (u: AccessMatrixUser): Promise<void> => {
    // A readable temp password: no 0/O/1/l/I to mis-relay, and it clears the >=8 floor.
    const abc = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
    const bytes = crypto.getRandomValues(new Uint8Array(9));
    const pw = `cg-${Array.from(bytes, (b) => abc[b % abc.length]).join('')}`;
    try {
      await api.post('/auth/admin/reset-password', { uid: u.user.id, newPassword: pw });
      setTempPw({ name: u.user.name, pw });
      toast(`Temporary password set for ${u.user.name}. Share it privately — they can change it after signing in.`, 'info');
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Could not reset the password.', 'error');
    }
  };

  /** Someone who knows an email can burn that account's sign-in attempts and lock
   *  the owner out of their OWN correct password for 15 minutes. This clears it.
   *  Safe by construction: it can only ever remove a block, never add one. */
  const clearSignInBlock = async (u: AccessMatrixUser): Promise<void> => {
    if (!u.user.email) { toast('No email on that account, so there is nothing to unblock.', 'error'); return; }
    try {
      const r = await api.post<{ wasCounting: boolean }>('/admin/login-throttle/clear', { email: u.user.email });
      toast(r.wasCounting
        ? `${u.user.name} can try signing in again now.`
        : `${u.user.name} wasn't blocked — whatever is stopping them is something else.`, 'info');
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Could not clear it.', 'error');
    }
  };

  /** Ends every session this person currently holds — phone, tablet, home laptop.
   *  Separate from "Remove all": that changes what they can SEE, this ends the
   *  sessions they are holding RIGHT NOW (a signed-in token survives access
   *  changes until it expires). */
  const signOutEverywhere = async (u: AccessMatrixUser): Promise<void> => {
    if (signingOut) return;
    setSigningOut(true);
    try {
      const r = await api.post<{ socketsDropped: number }>('/admin/sessions/revoke', { userId: u.user.id });
      toast(
        `${u.user.name} signed out everywhere${r.socketsDropped ? ` (${r.socketsDropped} live tab${r.socketsDropped > 1 ? 's' : ''} closed)` : ''}. They can sign back in unless their Odoo account is archived.`,
        'info',
      );
      setConfirmSignOut(false);
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Could not sign them out.', 'error');
    } finally {
      setSigningOut(false);
    }
  };

  const people = useMemo(() => {
    const query = q.trim().toLowerCase();
    return matrix.users
      .filter((u) => !query || u.user.name.toLowerCase().includes(query) || (u.user.email ?? '').toLowerCase().includes(query))
      .sort((a, b) => Number(b.isAdmin) - Number(a.isAdmin) || b.memberOf.length - a.memberOf.length || a.user.name.localeCompare(b.user.name));
  }, [matrix, q]);

  const selected = matrix.users.find((u) => u.user.id === selectedUid) ?? null;
  const areaName = (id: number) => matrix.areas.find((a) => a.id === id)?.name ?? `#${id}`;

  const hasAccess = selected ? [...selected.memberOf].sort((a, b) => areaName(a).localeCompare(areaName(b))) : [];
  const addable = selected
    ? matrix.areas
        .filter((a) => !selected.memberOf.includes(a.id))
        .filter((a) => !projQ.trim() || a.name.toLowerCase().includes(projQ.trim().toLowerCase()))
    : [];

  return (
    <div className="flex min-h-0 flex-1">
      <aside className="flex w-72 shrink-0 flex-col border-r border-line">
        <div className="p-2">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search people…"
            className="w-full rounded-md border border-line bg-canvas px-2.5 py-1.5 text-[13px] text-ink outline-none placeholder:text-faint focus:border-brand" />
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto pb-2">
          {people.map((u) => (
            <button key={u.user.id} onClick={() => setSelectedUid(u.user.id)}
              className={`flex w-full items-center gap-2 px-3 py-2 text-left ${selectedUid === u.user.id ? 'bg-active' : 'hover:bg-hover'}`}>
              <PersonDot name={u.user.name} id={u.user.id} />
              <span className="min-w-0 flex-1 truncate text-[13px] text-ink">{u.user.name}</span>
              {u.teamAllowed === false
                ? <span className="shrink-0 text-[10px] text-faint" title={teamRefusalWords(u.teamRefusal)}>No access</span>
                : lastSeen(u.lastLogin) && <span className="shrink-0 text-[10px] text-faint" title="Last signed in">{lastSeen(u.lastLogin)}</span>}
              {u.isAdmin ? <Chip tone="brand">Admin</Chip>
                : <span className={`shrink-0 text-[11px] ${u.memberOf.length === 0 ? 'text-faint' : 'text-muted'}`}>{u.memberOf.length}</span>}
            </button>
          ))}
        </div>
      </aside>

      <main className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
        {!selected && <div className="flex h-full items-center justify-center text-sm text-faint">Pick a person — add them to projects on the right.</div>}
        {selected && (
          <div className="mx-auto max-w-2xl">
            <div className="relative mb-4 flex items-center gap-2">
              <PersonDot name={selected.user.name} id={selected.user.id} size={8} />
              <div className="min-w-0 flex-1">
                <div className="truncate text-[15px] font-semibold text-ink">{selected.user.name}</div>
                {selected.user.email && <div className="truncate text-[11px] text-faint">{selected.user.email}</div>}
                {selected.teamAllowed === false && (
                  <div className="text-[11px] text-danger">Can’t open Projects: {teamRefusalWords(selected.teamRefusal)}</div>
                )}
              </div>
              {tempPw && tempPw.name === selected.user.name && (
                // Shown once, on THIS person, until you pick someone else. Not a toast:
                // a temporary password is the one thing here worth reading carefully and
                // copying, and a toast that vanishes in 4 seconds would lose it.
                <div className="absolute right-4 top-14 z-10 w-72 rounded-lg border border-brand/40 bg-canvas p-3 shadow-lg">
                  <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-brand">Temporary password</div>
                  <code className="mb-2 block select-all rounded bg-hover px-2 py-1.5 text-center text-[15px] font-semibold tracking-wider text-ink">{tempPw.pw}</code>
                  <p className="mb-2 text-[11px] text-faint">Give this to {tempPw.name} privately. They sign in with it, then change it in their own settings.</p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => { void navigator.clipboard?.writeText(tempPw.pw).then(() => toast('Copied.', 'info')).catch(() => undefined); }}
                      className="flex-1 rounded bg-brand px-2 py-1 text-[12px] font-semibold text-page hover:brightness-110">Copy</button>
                    <button onClick={() => setTempPw(null)} className="rounded border border-line px-2 py-1 text-[12px] text-muted hover:bg-hover hover:text-ink">Done</button>
                  </div>
                </div>
              )}
              {/* Available for admins too: the point is a lost or stolen device,
                  which doesn't care what access level the person has. */}
              {confirmSignOut ? (
                <span className="flex items-center gap-2 text-[12px]">
                  <span>Sign {selected.user.name} out of every device now?</span>
                  <button onClick={() => void signOutEverywhere(selected)} disabled={signingOut}
                    className="rounded bg-danger-solid px-2 py-1 font-semibold text-white disabled:opacity-40">Yes, sign out</button>
                  <button onClick={() => setConfirmSignOut(false)} className="text-muted hover:text-ink">Cancel</button>
                </span>
              ) : (
                <>
                  {passwordSignIn && (
                    <button onClick={() => void clearSignInBlock(selected)}
                      title="Use this when someone says they can't sign in and it says 'too many attempts'. It only ever removes a block."
                      className="shrink-0 rounded border border-line px-2 py-1 text-[12px] text-muted hover:bg-hover hover:text-ink">Can’t sign in?</button>
                  )}
                  <button onClick={() => { setConfirmAll(false); setConfirmSignOut(true); }}
                    title={passwordSignIn
                      ? 'Ends their sessions on every device. They can sign back in unless their account is switched off.'
                      : 'Ends their sessions on every device. They can come back from CG Team unless Projects is switched off for them there.'}
                    className="shrink-0 rounded border border-line px-2 py-1 text-[12px] text-muted hover:bg-hover hover:text-ink">Sign out</button>
                  {passwordSignIn && (
                    <button onClick={() => void resetPassword(selected)}
                      title="Set a temporary password for someone who is locked out. Shown once so you can pass it on."
                      className="shrink-0 rounded border border-line px-2 py-1 text-[12px] text-muted hover:bg-hover hover:text-ink">Reset password</button>
                  )}
                </>
              )}
              {!selected.isAdmin && selected.memberOf.length > 0 && !confirmSignOut && (
                confirmAll ? (
                  // Say exactly what this does — it also UNASSIGNS their open
                  // tasks (offboarding), which is not obvious from "Remove all".
                  <span className="flex items-center gap-2 text-[12px]">
                    <span>
                      Remove from all {selected.memberOf.length} project(s)
                      {(() => {
                        const n = selected.memberOf.filter((id) => !selected.managerOf.includes(id))
                          .reduce((sum, id) => sum + (selected.assignedIn?.[id] ?? 0), 0);
                        return n ? <> and unassign their <strong>{n} open task(s)</strong></> : null;
                      })()}?
                    </span>
                    <button onClick={() => { void removeAll(selected); setConfirmAll(false); }} disabled={busy}
                      className="rounded bg-danger-solid px-2 py-1 font-semibold text-white disabled:opacity-40">Yes, remove all</button>
                    <button onClick={() => setConfirmAll(false)} className="text-muted hover:text-ink">Cancel</button>
                  </span>
                ) : (
                  <button onClick={() => setConfirmAll(true)}
                    className="rounded border border-danger/40 px-2 py-1 text-[12px] text-danger hover:bg-danger/10">Remove all</button>
                )
              )}
            </div>

            {selected.isAdmin ? (
              <div className="rounded-md border border-brand/30 bg-brand/10 px-3 py-2 text-[13px] text-brand">
                Admin — sees every project automatically.
              </div>
            ) : (
              <>
                <section className="mb-5">
                  <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-faint">
                    Can see · {hasAccess.length}
                  </h3>
                  {hasAccess.length === 0 && (
                    <div className="rounded-md border border-dashed border-line px-3 py-3 text-center text-[12px] text-faint">
                      No projects yet — add some below.
                    </div>
                  )}
                  <div className="overflow-hidden rounded-lg border border-line bg-canvas">
                    {hasAccess.map((id) => {
                      const tasks = selected.assignedIn[id] ?? 0;
                      const leads = selected.managerOf.includes(id);
                      return (
                        <div key={id} className="flex items-center gap-2 border-b border-line px-3 py-2 last:border-0">
                          <span className="min-w-0 flex-1 truncate text-[13px] text-ink">{areaName(id)}</span>
                          {leads && <Chip tone="green" title="Project lead">lead</Chip>}
                          {tasks > 0 && <Chip title="Open tasks assigned to them here (info)">{tasks} task{tasks > 1 ? 's' : ''}</Chip>}
                          <button
                            onClick={() => (tasks > 0 || leads ? openOffboard(selected, id) : void removeMember(selected.user.id, id))}
                            disabled={busy}
                            title={tasks > 0 || leads ? 'They hold tasks or lead here — a quick dialog settles what happens' : 'Remove access'}
                            className="rounded border border-danger/40 px-1.5 py-0.5 text-[11px] text-danger hover:bg-danger/10 disabled:opacity-40">
                            − Remove{tasks > 0 || leads ? '…' : ''}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </section>

                <section>
                  <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-faint">Add to projects</h3>
                  <input value={projQ} onChange={(e) => setProjQ(e.target.value)} placeholder="Filter projects…"
                    className="mb-2 w-full rounded-md border border-line bg-canvas px-2.5 py-1.5 text-[13px] text-ink outline-none placeholder:text-faint focus:border-brand" />
                  <div className="overflow-hidden rounded-lg border border-line bg-canvas">
                    {addable.map((a) => {
                      const tasks = selected.assignedIn[a.id] ?? 0;
                      return (
                        <div key={a.id} className="flex items-center gap-2 border-b border-line px-3 py-2 last:border-0">
                          <span className="min-w-0 flex-1 truncate text-[13px] text-muted">{a.name}</span>
                          {tasks > 0 && <Chip title="They already have open tasks here — probably should be added">{tasks} task{tasks > 1 ? 's' : ''}</Chip>}
                          <button onClick={() => void addMember(selected.user.id, a.id)} disabled={busy}
                            className="flex items-center gap-1 rounded bg-brand px-2 py-0.5 text-[11px] font-semibold text-page transition hover:brightness-110 disabled:opacity-40">
                            <Icon i={Plus} size={14} /> Add
                          </button>
                        </div>
                      );
                    })}
                    {addable.length === 0 && <div className="px-3 py-3 text-center text-[12px] text-faint">No matches.</div>}
                  </div>
                </section>
              </>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------------
function ProjectsView({ matrix, busy, addMember, removeMember, openOffboard, changeLead }: {
  matrix: AccessMatrix;
  busy: boolean;
  addMember: (userId: number, areaId: number) => Promise<void>;
  removeMember: (userId: number, areaId: number) => Promise<void>;
  openOffboard: (u: AccessMatrixUser, areaId: number) => void;
  changeLead: (areaId: number, uid: number) => Promise<void>;
}) {
  const [q, setQ] = useState('');
  const [peopleQ, setPeopleQ] = useState('');
  const [selectedArea, setSelectedArea] = useState<number | null>(null);
  useEffect(() => { setPeopleQ(''); }, [selectedArea]);

  const memberCount = (areaId: number) => matrix.users.filter((u) => !u.isAdmin && u.memberOf.includes(areaId)).length;

  const areas = useMemo(() => {
    const query = q.trim().toLowerCase();
    return matrix.areas.filter((a) => !query || a.name.toLowerCase().includes(query));
  }, [matrix, q]);

  const area = matrix.areas.find((a) => a.id === selectedArea) ?? null;
  const roster = area ? matrix.users.filter((u) => !u.isAdmin && u.memberOf.includes(area.id)).sort((a, b) => a.user.name.localeCompare(b.user.name)) : [];
  const addable = area
    ? matrix.users
        .filter((u) => !u.isAdmin && !u.memberOf.includes(area.id))
        .filter((u) => !peopleQ.trim() || u.user.name.toLowerCase().includes(peopleQ.trim().toLowerCase()))
        .sort((a, b) => (b.assignedIn[area.id] ?? 0) - (a.assignedIn[area.id] ?? 0) || a.user.name.localeCompare(b.user.name))
    : [];

  return (
    <div className="flex min-h-0 flex-1">
      <aside className="flex w-72 shrink-0 flex-col border-r border-line">
        <div className="p-2">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search projects…"
            className="w-full rounded-md border border-line bg-canvas px-2.5 py-1.5 text-[13px] text-ink outline-none placeholder:text-faint focus:border-brand" />
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto pb-2">
          {areas.map((a) => (
            <button key={a.id} onClick={() => setSelectedArea(a.id)}
              className={`flex w-full items-center gap-2 px-3 py-2 text-left ${selectedArea === a.id ? 'bg-active' : 'hover:bg-hover'}`}>
              <span className="min-w-0 flex-1 truncate text-[13px] text-ink">{a.name}</span>
              <span className={`shrink-0 text-[11px] ${memberCount(a.id) === 0 ? 'text-faint' : 'text-muted'}`}>{memberCount(a.id)}</span>
            </button>
          ))}
        </div>
      </aside>

      <main className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
        {!area && <div className="flex h-full items-center justify-center text-sm text-faint">Pick a project — build its team on the right.</div>}
        {area && (
          <div className="mx-auto max-w-2xl">
            <h2 className="mb-1 text-[15px] font-semibold text-ink">{area.name}</h2>
            {(() => {
              const lead = matrix.users.find((u) => u.managerOf.includes(area.id));
              const leadCandidates = matrix.users
                .filter((u) => u.isAdmin || u.memberOf.includes(area.id))
                .sort((a, b) => a.user.name.localeCompare(b.user.name));
              return (
                <div className="mb-4 flex items-center gap-2 text-[12px] text-muted">
                  Lead: <span className="font-medium text-ink">{lead?.user.name ?? '— none —'}</span>
                  <select
                    value=""
                    disabled={busy}
                    onChange={(e) => { const v = Number(e.target.value); if (v) void changeLead(area.id, v); }}
                    className="rounded border border-line bg-canvas px-1.5 py-0.5 text-[11px] text-muted outline-none hover:text-ink"
                    title="Hand this project to a new lead (they become a member automatically)"
                  >
                    <option value="">change…</option>
                    {leadCandidates.filter((c) => c.user.id !== lead?.user.id).map((c) => (
                      <option key={c.user.id} value={c.user.id}>{c.user.name}</option>
                    ))}
                  </select>
                </div>
              );
            })()}

            <section className="mb-5">
              <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-faint">Team · {roster.length} <span className="normal-case">(+ admins)</span></h3>
              {roster.length === 0 && (
                <div className="rounded-md border border-dashed border-line px-3 py-3 text-center text-[12px] text-faint">
                  Nobody yet — add people below.
                </div>
              )}
              <div className="overflow-hidden rounded-lg border border-line bg-canvas">
                {roster.map((u) => {
                  const tasks = u.assignedIn[area.id] ?? 0;
                  const leads = u.managerOf.includes(area.id);
                  return (
                    <div key={u.user.id} className="flex items-center gap-2 border-b border-line px-3 py-2 last:border-0">
                      <PersonDot name={u.user.name} id={u.user.id} />
                      <span className="min-w-0 flex-1 truncate text-[13px] text-ink">{u.user.name}</span>
                      {leads && <Chip tone="green" title="Project lead">lead</Chip>}
                      {tasks > 0 && <Chip>{tasks} task{tasks > 1 ? 's' : ''}</Chip>}
                      <button
                        onClick={() => (tasks > 0 || leads ? openOffboard(u, area.id) : void removeMember(u.user.id, area.id))}
                        disabled={busy}
                        title={tasks > 0 || leads ? 'They hold tasks or lead here — a quick dialog settles what happens' : 'Remove access'}
                        className="rounded border border-danger/40 px-1.5 py-0.5 text-[11px] text-danger hover:bg-danger/10 disabled:opacity-40">
                        − Remove{tasks > 0 || leads ? '…' : ''}
                      </button>
                    </div>
                  );
                })}
              </div>
            </section>

            <section>
              <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-faint">Add people</h3>
              <input value={peopleQ} onChange={(e) => setPeopleQ(e.target.value)} placeholder="Filter people…"
                className="mb-2 w-full rounded-md border border-line bg-canvas px-2.5 py-1.5 text-[13px] text-ink outline-none placeholder:text-faint focus:border-brand" />
              <div className="overflow-hidden rounded-lg border border-line bg-canvas">
                {addable.map((u) => {
                  const tasks = u.assignedIn[area.id] ?? 0;
                  return (
                    <div key={u.user.id} className="flex items-center gap-2 border-b border-line px-3 py-2 last:border-0">
                      <PersonDot name={u.user.name} id={u.user.id} />
                      <span className="min-w-0 flex-1 truncate text-[13px] text-muted">{u.user.name}</span>
                      {tasks > 0 && <Chip title="Already has open tasks here — probably belongs on the team">{tasks} task{tasks > 1 ? 's' : ''}</Chip>}
                      <button onClick={() => void addMember(u.user.id, area.id)} disabled={busy}
                        className="flex items-center gap-1 rounded bg-brand px-2 py-0.5 text-[11px] font-semibold text-page transition hover:brightness-110 disabled:opacity-40">
                        <Icon i={Plus} size={14} /> Add
                      </button>
                    </div>
                  );
                })}
                {addable.length === 0 && <div className="px-3 py-3 text-center text-[12px] text-faint">No matches.</div>}
              </div>
            </section>
          </div>
        )}
      </main>
    </div>
  );
}
